import React, { useState, useEffect } from 'react'
import { useTheme } from 'styled-components'
import { useLocation } from 'react-router-dom'
import {
  BottomSheet,
  LazyImage,
  GuestUserModal,
  useToast,
  Text
} from 'shared-ui/components'
import {
  BookmarkIcon,
  reportIcon,
  accountIcon,
  SavedContent,
  DownloadIcon
} from 'shared-ui/assets'
import {
  useSaveContent,
  useDiscoveDeleteContent,
  useDiscoverData
} from 'shared-ui/hooks'
import { getGuestUser } from 'shared-ui/utils/localStorage'
import {
  ROUTES,
  SUCCESS_MESSAGE,
  ERROR_MESSAGES,
  PROFILE_CONSTANTS,
  QUERY_KEY,
  SUPER_APP_CONFIG,
  DASHBOARD_MESSAGES
} from 'shared-ui/utils/constants'
import {
  AboutAccountSheet,
  AboutAccountTitle,
  AboutAccountDetails,
  AboutAccountRow
} from '../Profile/styles'
import { format } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import { GA_EVENTS } from 'shared-ui/utils/gaConstants'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'

const ActionSheet = ({ open, onClose, user, data, onReportOpen, fromDashboard = false, fromNotYetPublished = false, onDownload, onSaveToggle,isCurrentUser}) => {
  const theme = useTheme()
  const { showError, showSuccess } = useToast()
  const location = useLocation()
  const [dataVal, setData] = useState(data)
  const [showGuestModal, setShowGuestModal] = useState(false)
  const [isAccountInfoOpen, setAccountInfoOpen] = useState(false)
  const { refetch } = useDiscoverData()
  const queryClient = useQueryClient()
  const trackEvent = useTrackEvent()

  useEffect(() => {
    const normalizeData = (d) => {
      if (!d) return d
      return {
        ...d,
        content_id: d?.content_id || d?.id || d?.contentId,
        user_id: d?.user_id || d?.userId || d?.creator_id
      }
    }

    setData(normalizeData(data))
    // refetch()
  }, [data])

  const { mutateAsync: saveContentMutate, isLoading: saveLoading } =
    useSaveContent(user?.user_id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['GET_USER_DASHBOARD', user?.user_id, 'saved', data?.content_type, 'saved'] })
        showSuccess(SUCCESS_MESSAGE?.SAVED_TO_DASHBOARD || 'Saved to My Dashboard')
      },
      onError: (err) => {
        const parsed = JSON.parse(err?.message)
        showError(parsed || ERROR_MESSAGES.FAILED_TO_SAVE)
      }
    })

  const { mutate: deleteMutation } = useDiscoveDeleteContent({
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['GET_USER_DASHBOARD'] })
    },
    onError: (err) => {
      showError(err?.message || ERROR_MESSAGES.FAILED_TO_REMOVE_SAVES)
    }
  })

  const checkGuest = () => {
    if (getGuestUser() === 'true') {
      setShowGuestModal(true)
      return true
    } else {
      return false
    }
  }

  const handleSave = async (content_id, user_id) => {
    if (checkGuest()) return
    if (saveLoading) return
    if (!user?.user_id) {
      showError(ERROR_MESSAGES.LOGIN_TO_SAVE)
      return
    }
    const cid = content_id || dataVal?.content_id || dataVal?.id
    const uid = user_id || dataVal?.user_id || dataVal?.userId

    if (!cid) {
      showError(ERROR_MESSAGES.CONTENT_NOT_FOUND)
      return
    }
    trackEvent(GA_EVENTS.content_saved, {
      content_id: cid,
      creator_id: uid
    })

    try {
      const res = await saveContentMutate(cid)
      queryClient.invalidateQueries(QUERY_KEY.GET_DISCOVER_DATA)
      queryClient.invalidateQueries(['GET_CONTENT_DETAILS_BY_ID', cid])
      queryClient.invalidateQueries(['GET_SAVED_CONTENT'])
      onClose && onClose({ content_id: cid, is_saved: true, serverResponse: res })
    } catch {
      // error handled in onError
    } finally {
      onClose && onClose()
    }
  }

  const handleUnSave = async (content_id) => {
    if (checkGuest()) return
    if (saveLoading) return
    if (!user?.user_id) {
      showError(ERROR_MESSAGES.LOGIN_TO_SAVE)
      return
    }
    const cid = content_id || dataVal?.content_id || dataVal?.id
    if (!cid) {
      showError(ERROR_MESSAGES.CONTENT_NOT_FOUND)
      return
    }

    try {
      const res = await deleteMutation(cid)
      queryClient.invalidateQueries(QUERY_KEY.GET_DISCOVER_DATA)
      queryClient.invalidateQueries(['GET_CONTENT_DETAILS_BY_ID', cid])
      queryClient.invalidateQueries(['GET_SAVED_CONTENT'])
      onClose && onClose({ content_id: cid, is_saved: false, serverResponse: res })
    } catch {
      // ignore
    } finally {
      onClose && onClose()
    }
  }

  const handleToggleSave = (is_saved, content_id, user_id) => {
    if (checkGuest()) return

    if (onSaveToggle) {
      onSaveToggle(is_saved, content_id, user_id)
      return
    }

    if (is_saved) handleUnSave(content_id)
    else handleSave(content_id, user_id)

    // Optimistic UI update
    setData((prevData) => {
      if (!prevData) return prevData
      const newData = { ...prevData }

      // Toggle top-level is_saved
      newData.is_saved = !is_saved

      // Toggle nested assets
      const postsTray = newData.data?.trays?.find(
        (tray) => tray.trayType === 'posts'
      )
      if (postsTray && postsTray.assets) {
        postsTray.assets = postsTray.assets.map((p) =>
          (p.content_id || p.id) === content_id ? { ...p, is_saved: !p.is_saved } : p
        )
      }

      return newData
    })
  }

  const getFormattedDate = () => {
    if (!data?.created_at) return ''
    try {
      return format(new Date(data?.created_at), 'dd MMMM yyyy')
    } catch {
      return ''
    }
  }

  const handleAbout = () => {
    if (checkGuest()) return
    setAccountInfoOpen(true)
  }

  const handleReport = () => {
    if (checkGuest()) return
    onClose()
    setTimeout(() => onReportOpen?.(), 300)
  }
  const isDiscoverPage = location.pathname.startsWith(ROUTES.discover)

  const isOwnAccount = user?.user_id === dataVal?.user_id

  const options = [
    // Conditionally include Save/Unsave option
    ...(data?.is_ugc && !fromNotYetPublished
      ? [
        {
          icon: dataVal?.is_saved ? (
            <LazyImage src={SavedContent} alt="BookmarkIcon" />
          ) : (
            <LazyImage src={BookmarkIcon} alt="BookmarkIcon" />
          ),
          title: dataVal?.is_saved ? DASHBOARD_MESSAGES?.UNSAVE : SUPER_APP_CONFIG?.SAVE,
          onClick: () =>
            handleToggleSave(dataVal?.is_saved, dataVal?.content_id, dataVal?.user_id)
        }
      ]
      : []),
    ...(!fromNotYetPublished && !isOwnAccount
      ? [
        {
          icon: <LazyImage src={accountIcon} alt="accountIcon" />,
          title: PROFILE_CONSTANTS?.TEXT?.ABOUT_ACCOUNT,
          onClick: handleAbout
        }
      ]
      : []),
    ...(!fromNotYetPublished && !isOwnAccount
      ? [
        {
          icon: <LazyImage src={reportIcon} alt="reportIcon" />,
          title: PROFILE_CONSTANTS?.TEXT?.REPORT,
          color: '#ff3333',
          onClick: handleReport
        }
      ]
      : []),
    ...(fromDashboard && onDownload && isCurrentUser
      ? [
        {
          icon: (
            <img
              src={DownloadIcon}
              style={{
                width: 24,
                height: 24,
                filter: 'brightness(1.5)' // Make it pop a bit more as it is a generic icon
              }}
              alt="Download"
            />
          ),
          title: DASHBOARD_MESSAGES?.DOWNLOAD,
          onClick: () => {
            onClose()
            onDownload()
          }
        }
      ]
      : [])
  ]

  return (
    <>
      {!isAccountInfoOpen ? (
        <BottomSheet open={open} onClose={onClose}>
          <div className="flex flex-col">
            {options.map((item) => {
              const isLoading =
                saveLoading &&
                item.title === (dataVal?.is_saved ? 'Unsave' : 'Save')
              return (
                <div
                  key={item.title}
                  className={`flex items-center gap-4 py-4 ${item.disabled || isLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                    }`}
                  onClick={
                    item.disabled || isLoading ? undefined : item.onClick
                  }
                  style={{ color: item.color || theme.app.iconlabel }}
                >
                  {item.icon}
                  <span className="text-base font-medium">{item.title}</span>
                  {isLoading && (
                    <span className="text-sm text-gray-500">Saving...</span>
                  )}
                </div>
              )
            })}
          </div>
        </BottomSheet>
      ) : (
        <BottomSheet
          open={isAccountInfoOpen}
          onClose={() => {
            ; (setAccountInfoOpen(false), onClose())
          }}
          closeButtonStyle={{ top: '2.7rem' }}
        >
          <AboutAccountSheet>
            <AboutAccountTitle>
              {PROFILE_CONSTANTS.TEXT.ABOUT_ACCOUNT}
            </AboutAccountTitle>
            <AboutAccountDetails>
              <AboutAccountRow>
                <Text color={theme?.app?.iconlabel} fontSize={'14px'}>
                  {PROFILE_CONSTANTS.TEXT.DATE_JOINED}
                </Text>
                <Text color={theme?.app?.text} fontSize={'14px'}>
                  {getFormattedDate()}
                </Text>
              </AboutAccountRow>
              <AboutAccountRow>
                <Text color={theme?.app?.iconlabel} fontSize={'14px'}>
                  {PROFILE_CONSTANTS.TEXT.ACCOUNT_BASED_IN}
                </Text>
                <Text color={theme?.app?.text} fontSize={'14px'}>
                  IN
                </Text>
              </AboutAccountRow>
            </AboutAccountDetails>
          </AboutAccountSheet>
        </BottomSheet>
      )}
      {showGuestModal && (
        <GuestUserModal onClose={() => setShowGuestModal(false)} />
      )}
    </>
  )
}

export default ActionSheet
